import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <h1>Privacy Policy</h1>
      <p className="effective-date"><strong>Effective Date:</strong> 22nd July 2026</p>

      <p>
        Welcome to YogaRefer ("YogaRefer", "we", "our", or "us"). We value your privacy and are
        committed to protecting your personal information. This Privacy Policy explains how we
        collect, use, store, disclose, and safeguard your information when you access or use our
        website.
      </p>
      <p>
        By accessing or using YogaRefer, you agree to the collection and use of your information
        in accordance with this Privacy Policy.
      </p>

      <h2>1. About YogaRefer</h2>
      <p>
        YogaRefer is an online platform that provides educational information about yoga asanas
        and allows registered contributors to showcase their yoga photographs on selected asana
        pages. Contributors may purchase placement for their images and profile links, subject to
        the applicable subscription period and platform policies.
      </p>
      <p>
        YogaRefer also displays advertisements through Google AdSense and uses Google Analytics to
        improve user experience.
      </p>

      <h2>2. Information We Collect</h2>
      <h3>A. Information You Provide</h3>
      <p>
        When you register an account, purchase a contributor listing, contact us, or upload
        content, we may collect:
      </p>
      <ul>
        <li>Full Name</li>
        <li>Email Address</li>
        <li>Username</li>
        <li>Password (stored securely in encrypted form)</li>
        <li>Country and State (if provided)</li>
        <li>Profile photograph</li>
        <li>Biography</li>
        <li>Website URL</li>
        <li>Social media links</li>
        <li>Yoga experience or qualifications (optional)</li>
        <li>Uploaded yoga photographs</li>
        <li>Payment-related information required to complete transactions</li>
        <li>Messages submitted through contact forms</li>
      </ul>

      <h3>B. Information Automatically Collected</h3>
      <p>
        When you visit YogaRefer, certain technical information may be collected automatically,
        including:
      </p>
      <ul>
        <li>IP Address</li>
        <li>Browser type</li>
        <li>Device information</li>
        <li>Operating system</li>
        <li>Referring website</li>
        <li>Pages visited</li>
        <li>Time spent on pages</li>
        <li>Click behaviour</li>
        <li>Date and time of visit</li>
      </ul>
      <p>This information helps us improve website performance and user experience.</p>

      <h3>C. Cookies and Tracking Technologies</h3>
      <p>YogaRefer uses cookies and similar technologies to:</p>
      <ul>
        <li>Maintain website functionality</li>
        <li>Improve website performance</li>
        <li>Understand visitor behaviour</li>
        <li>Measure website traffic</li>
        <li>Display personalised advertisements through Google AdSense</li>
      </ul>
      <p>
        You may disable cookies through your browser settings; however, certain features of the
        website may not function properly.
      </p>

      <h2>3. Google Analytics</h2>
      <p>YogaRefer uses Google Analytics to understand how visitors interact with our website.</p>
      <p>Google Analytics may collect information such as:</p>
      <ul>
        <li>Pages viewed</li>
        <li>Session duration</li>
        <li>Device type</li>
        <li>Browser</li>
        <li>Geographic region</li>
        <li>Traffic source</li>
      </ul>
      <p>
        This information is collected in an aggregated manner and is not intended to personally
        identify users.
      </p>

      <h2>4. Google AdSense</h2>
      <p>YogaRefer participates in the Google AdSense advertising program.</p>
      <p>Google and its advertising partners may use cookies to:</p>
      <ul>
        <li>Display relevant advertisements</li>
        <li>Measure advertisement performance</li>
        <li>Limit repetitive advertisements</li>
        <li>Personalise advertising based on browsing behaviour</li>
      </ul>
      <p>
        Third-party vendors, including Google, use cookies to serve ads based on a user's previous
        visits to this website and other websites. Users can learn more about Google's advertising
        practices and manage ad personalization through Google's Ad Settings.
      </p>

      <h2>5. Contributor Information</h2>
      <p>Registered contributors may voluntarily submit:</p>
      <ul>
        <li>Yoga photographs</li>
        <li>Personal profile</li>
        <li>Website links</li>
        <li>Social media profiles</li>
        <li>Biography</li>
      </ul>
      <p>
        By submitting this information, contributors acknowledge that the information they choose
        to publish may become publicly visible on YogaRefer. YogaRefer is not responsible for
        information that contributors voluntarily choose to display publicly.
      </p>

      <h2>6. Payments</h2>
      <p>Contributor subscriptions require online payment.</p>
      <p>
        Payment processing is handled securely through our authorised payment gateway. YogaRefer
        does not store complete debit card, credit card, UPI PIN, CVV, or banking credentials on
        its own servers. Payment gateways may collect and process payment information according to
        their own privacy policies.
      </p>

      <h2>7. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Create and manage user accounts</li>
        <li>Verify contributor identity</li>
        <li>Process contributor subscriptions</li>
        <li>Publish contributor profiles</li>
        <li>Display uploaded yoga photographs</li>
        <li>Improve website functionality</li>
        <li>Respond to enquiries</li>
        <li>Detect fraudulent activity</li>
        <li>Maintain website security</li>
        <li>Analyse website usage</li>
        <li>Comply with applicable legal obligations</li>
      </ul>

      <h2>8. Content Uploaded by Contributors</h2>
      <p>Contributors remain responsible for all content they upload.</p>
      <p>By uploading photographs and profile information, contributors confirm that:</p>
      <ul>
        <li>They own the content or have permission to use it.</li>
        <li>The content does not infringe copyright or intellectual property rights.</li>
        <li>The content complies with YogaRefer's platform policies.</li>
      </ul>
      <p>
        YogaRefer reserves the right to review, reject, edit, or remove any submitted content that
        violates our policies.
      </p>

      <h2>9. Sharing of Information</h2>
      <p>YogaRefer does not sell users' personal information.</p>
      <p>We may share information only with trusted service providers who assist in operating our website, such as:</p>
      <ul>
        <li>Payment gateway providers</li>
        <li>Website hosting providers</li>
        <li>Cloud storage providers</li>
        <li>Analytics providers</li>
        <li>Advertising partners</li>
        <li>Legal authorities when required by law</li>
      </ul>

      <h2>10. Third-Party Links</h2>
      <p>Contributor profiles may include personal websites or social media links.</p>
      <p>
        YogaRefer is not responsible for the privacy practices, security, or content of
        third-party websites. Users should review the privacy policies of any external websites
        they visit.
      </p>

      <h2>11. Data Security</h2>
      <p>
        We implement commercially reasonable technical and organisational measures to protect user
        information against unauthorised access, misuse, alteration, or disclosure. However, no
        method of electronic transmission or online storage is completely secure, and we cannot
        guarantee absolute security.
      </p>

      <h2>12. Data Retention</h2>
      <p>We retain personal information only for as long as necessary to:</p>
      <ul>
        <li>Maintain user accounts</li>
        <li>Fulfil contributor subscriptions</li>
        <li>Meet legal obligations</li>
        <li>Resolve disputes</li>
        <li>Prevent fraud</li>
        <li>Improve our services</li>
      </ul>
      <p>
        Contributor content may remain available until removed in accordance with our Terms &amp;
        Conditions.
      </p>

      <h2>13. Account Suspension and Removal</h2>
      <p>YogaRefer reserves the right to suspend or terminate accounts that:</p>
      <ul>
        <li>Violate our Terms &amp; Conditions</li>
        <li>Upload misleading or unlawful content</li>
        <li>Infringe intellectual property rights</li>
        <li>Abuse the platform</li>
        <li>Engage in fraudulent activity</li>
      </ul>
      <p>Where appropriate, published contributor content may also be removed.</p>

      <h2>14. Children's Privacy</h2>
      <p>YogaRefer is intended for users aged 18 years or older.</p>
      <p>
        We do not knowingly collect personal information from children under the age of 18. If we
        become aware that such information has been collected without appropriate consent, we will
        take reasonable steps to remove it.
      </p>

      <h2>15. Your Rights</h2>
      <p>Subject to applicable law, users may have the right to:</p>
      <ul>
        <li>Access their personal information</li>
        <li>Request correction of inaccurate information</li>
        <li>Request copies of information we hold</li>
        <li>Object to certain processing activities</li>
        <li>Request restriction of processing where applicable</li>
      </ul>
      <p>Requests may be submitted using the contact information provided below.</p>

      <h2>16. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Updated versions will be published on
        this page with a revised Effective Date. Continued use of YogaRefer after changes become
        effective constitutes acceptance of the updated Privacy Policy.
      </p>

      <h2>17. Contact Us</h2>
      <p>If you have any questions regarding this Privacy Policy or your personal information, please contact:</p>
      <p>
        YogaRefer<br />
        Email: <a href="mailto:help.yogarefer@gmail.com">help.yogarefer@gmail.com</a><br />
        Website: <a href="https://yogarefer.com">https://yogarefer.com</a><br />
        Business Address: 3/66, Sakkudi, Madurai
      </p>
      <p>We will make reasonable efforts to respond to privacy-related enquiries in a timely manner.</p>
    </div>
  );
};

export default PrivacyPolicy;